import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  History, 
  Search, 
  Filter, 
  User, 
  Calendar, 
  FileText,
  Activity,
  LogIn,
  LogOut,
  Edit,
  Trash2,
  Download,
  Key,
  RefreshCw
} from 'lucide-react';

export default function AuditLogsPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    action: '',
    entity_type: '',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.action) params.append('action', filters.action);
      if (filters.entity_type) params.append('entity_type', filters.entity_type);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);

      const response = await api.get(`/audit-logs?${params.toString()}`);
      setLogs(response.data);
    } catch (error) {
      toast.error('Erro ao carregar logs de auditoria');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      action: '',
      entity_type: '',
      start_date: '',
      end_date: ''
    });
  };

  const getActionIcon = (action) => {
    const icons = {
      'CREATE': <FileText className="h-4 w-4 text-green-600" />,
      'UPDATE': <Edit className="h-4 w-4 text-blue-600" />,
      'DELETE': <Trash2 className="h-4 w-4 text-red-600" />,
      'LOGIN': <LogIn className="h-4 w-4 text-teal-600" />,
      'LOGOUT': <LogOut className="h-4 w-4 text-slate-600" />,
      'EXPORT': <Download className="h-4 w-4 text-purple-600" />,
      'PASSWORD_CHANGE': <Key className="h-4 w-4 text-amber-600" />,
      'STATUS_CHANGE': <RefreshCw className="h-4 w-4 text-sky-600" />,
    };
    return icons[action] || <Activity className="h-4 w-4 text-slate-400" />;
  };

  const getActionColor = (action) => {
    const colors = {
      'CREATE': 'bg-green-100 text-green-800',
      'UPDATE': 'bg-blue-100 text-blue-800',
      'DELETE': 'bg-red-100 text-red-800',
      'LOGIN': 'bg-teal-100 text-teal-800',
      'LOGOUT': 'bg-slate-100 text-slate-800',
      'EXPORT': 'bg-purple-100 text-purple-800',
      'PASSWORD_CHANGE': 'bg-amber-100 text-amber-800',
      'STATUS_CHANGE': 'bg-sky-100 text-sky-800',
    };
    return colors[action] || 'bg-slate-100 text-slate-800';
  };

  const getEntityColor = (entity) => {
    const colors = {
      'user': 'bg-violet-100 text-violet-800',
      'patient': 'bg-emerald-100 text-emerald-800',
      'appointment': 'bg-sky-100 text-sky-800',
      'unit': 'bg-amber-100 text-amber-800',
      'report': 'bg-rose-100 text-rose-800',
      'system': 'bg-slate-100 text-slate-800',
    };
    return colors[entity] || 'bg-slate-100 text-slate-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== '');

  return (
    <div data-testid="audit-logs-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-slate-900 mb-2 tracking-tight">Logs de Auditoria</h1>
        <p className="text-slate-600">Histórico de todas as ações realizadas no sistema</p>
      </div>

      {/* Filtros */}
      <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="h-5 w-5 text-slate-600" />
          <h3 className="font-semibold text-slate-900">Filtros</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs">Ação</Label>
            <Select value={filters.action} onValueChange={(value) => setFilters({ ...filters, action: value === 'all' ? '' : value })}>
              <SelectTrigger className="h-10 rounded-lg mt-1">
                <SelectValue placeholder="Todas as ações" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as ações</SelectItem>
                <SelectItem value="CREATE">Criação</SelectItem>
                <SelectItem value="UPDATE">Atualização</SelectItem>
                <SelectItem value="DELETE">Exclusão</SelectItem>
                <SelectItem value="LOGIN">Login</SelectItem>
                <SelectItem value="EXPORT">Exportação</SelectItem>
                <SelectItem value="PASSWORD_CHANGE">Alteração de Senha</SelectItem>
                <SelectItem value="STATUS_CHANGE">Mudança de Status</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">Tipo de Entidade</Label>
            <Select value={filters.entity_type} onValueChange={(value) => setFilters({ ...filters, entity_type: value === 'all' ? '' : value })}>
              <SelectTrigger className="h-10 rounded-lg mt-1">
                <SelectValue placeholder="Todos os tipos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os tipos</SelectItem>
                <SelectItem value="user">Usuário</SelectItem>
                <SelectItem value="patient">Paciente</SelectItem>
                <SelectItem value="appointment">Consulta</SelectItem>
                <SelectItem value="unit">Unidade</SelectItem>
                <SelectItem value="report">Relatório</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">Data Inicial</Label>
            <Input
              type="date"
              value={filters.start_date}
              onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
              className="h-10 rounded-lg mt-1"
            />
          </div>

          <div>
            <Label className="text-xs">Data Final</Label>
            <Input
              type="date"
              value={filters.end_date}
              onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
              className="h-10 rounded-lg mt-1"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-4">
          <Button onClick={fetchLogs} className="bg-teal-700 hover:bg-teal-800">
            <Search className="h-4 w-4 mr-2" />
            Buscar
          </Button>
          {hasActiveFilters && (
            <Button variant="outline" onClick={clearFilters}>
              Limpar Filtros
            </Button>
          )}
        </div>
      </div>

      {/* Lista de Logs */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <History className="h-5 w-5 text-teal-600" />
              <span className="font-semibold text-slate-900">Registros de Atividade</span>
            </div>
            <span className="text-sm text-slate-500">{logs.length} registros</span>
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center text-slate-500">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-teal-600" />
            Carregando logs...
          </div>
        ) : logs.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <History className="h-12 w-12 mx-auto mb-4 text-slate-300" />
            Nenhum log encontrado
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {logs.map((log) => (
              <div key={log.id} className="p-4 hover:bg-slate-50 transition-colors">
                <div className="flex items-start gap-4">
                  <div className="mt-1">
                    {getActionIcon(log.action)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getActionColor(log.action)}`}>
                        {log.action}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getEntityColor(log.entity_type)}`}>
                        {log.entity_type}
                      </span>
                    </div>
                    <p className="text-sm text-slate-900 font-medium mb-1">{log.description}</p>
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {log.user_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDate(log.timestamp)}
                      </span>
                    </div>
                    {(log.old_value || log.new_value) && (
                      <div className="mt-2 p-2 bg-slate-50 rounded text-xs">
                        {log.old_value && (
                          <div className="text-slate-600">
                            <span className="font-medium">Antes:</span> {JSON.stringify(log.old_value)}
                          </div>
                        )}
                        {log.new_value && (
                          <div className="text-slate-600">
                            <span className="font-medium">Depois:</span> {JSON.stringify(log.new_value)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
